import { PageHeader, LoadingState, DataTable, EmptyState } from '../components/AdminPageUI';
import SummaryStrip, { SummaryMetric } from '../components/SummaryStrip';
import StudentAvatar from '../components/StudentAvatar';
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Grid, Button, MenuItem, TextField, Alert,
  Card, CardContent, Container, Avatar, Stack, Chip, CircularProgress,
  Paper, Divider, Checkbox, FormControlLabel, Switch, Table,
  TableHead, TableRow, TableCell, TableBody, Tooltip, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions, useMediaQuery,
  alpha, useTheme
} from '@mui/material';
import {
  School, ArrowForward, ExpandMore, ExpandLess,
  Group, Person, CalendarToday, Class, CheckCircle,
  Warning, Info, PlaylistAddCheck
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../utils/Api';

const terms = ['Term 1', 'Term 2', 'Term 3'];
const ALL_REPORT_CARD_CLASSES = '__all_report_card_classes__';
const CLASS_ORDER = [
  'CRECHE',
  'NURSERY 1', 'NURSERY 2',
  'KG 1', 'KG 2',
  'BASIC 1', 'BASIC 2', 'BASIC 3', 'BASIC 4',
  'BASIC 5', 'BASIC 6', 'BASIC 7', 'BASIC 8', 'BASIC 9'
];

const generateFallbackAcademicYears = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = -3; i <= 3; i++) {
    years.push(`${currentYear + i}-${currentYear + i + 1}`);
  }
  return years;
};

/* --------------------------
   Academic Year Helpers
--------------------------- */

const toAcademicYearString = (val) => {
  if (!val && val !== 0) return "";
  if (typeof val === "string" && /^\d{4}-\d{4}$/.test(val.trim())) return val.trim();
  if (typeof val === "string" && /\d{4}\s+to\s+\d{4}/i.test(val.trim())) {
    return val.trim().replace(/\s+to\s+/i, '-');
  }
  const y = parseInt(val, 10);
  if (Number.isNaN(y)) return "";
  return `${y}-${y + 1}`;
};

// Current year formatted
const currentAcademicYearString = () => {
  const y = new Date().getFullYear();
  return `${y}-${y + 1}`;
};

// Next year formatted
const nextAcademicYearString = () => {
  const y = new Date().getFullYear() + 1;
  return `${y}-${y + 1}`;
};

const getStoredSchoolId = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.school?._id || user?.school?.id || user?.school || '';
  } catch (error) {
    return '';
  }
};

const getNextAcademicYear = (year) => {
  const normalized = toAcademicYearString(year);
  const [start] = normalized.split('-').map(Number);
  if (!start || Number.isNaN(start)) return nextAcademicYearString();
  return `${start + 1}-${start + 2}`;
};

const normalizeClassLabel = (value) =>
  String(value || '').toUpperCase().replace(/\s+/g, ' ').trim();

const parseClassLabel = (value) => {
  const normalized = normalizeClassLabel(value);
  if (!normalized) return { baseName: '', stream: '' };

  for (const className of CLASS_ORDER) {
    const baseName = normalizeClassLabel(className);
    const compactBaseName = baseName.replace(/\s+/g, '');
    const compactLabel = normalized.replace(/\s+/g, '');

    if (normalized === baseName || compactLabel === compactBaseName) {
      return { baseName, stream: '' };
    }

    if (normalized.startsWith(`${baseName} `)) {
      const stream = normalized.slice(baseName.length).trim();
      if (/^[A-Z]$/.test(stream)) return { baseName, stream };
    }

    if (compactLabel.startsWith(compactBaseName)) {
      const stream = compactLabel.slice(compactBaseName.length);
      if (/^[A-Z]$/.test(stream)) return { baseName, stream };
    }
  }

  return { baseName: normalized, stream: '' };
};

const getClassDisplayName = (classItem) =>
  classItem?.displayName || classItem?.name || 'Unnamed Class';

const getClassBaseName = (classItem) => {
  const possibleNames = [classItem?.name, classItem?.displayName]
    .map(normalizeClassLabel)
    .filter(Boolean);

  for (const name of possibleNames) {
    const { baseName } = parseClassLabel(name);
    if (CLASS_ORDER.map(normalizeClassLabel).includes(baseName)) return baseName;
  }

  return possibleNames[0] || '';
};

const getNextClassBaseName = (classItem) => {
  const currentBase = getClassBaseName(classItem);
  const index = CLASS_ORDER.map(normalizeClassLabel).findIndex((item) => item === currentBase);
  return index >= 0 ? normalizeClassLabel(CLASS_ORDER[index + 1]) : '';
};

const sortClassesByDisplayName = (items) =>
  [...items].sort((a, b) => getClassDisplayName(a).localeCompare(getClassDisplayName(b)));

const getStudentDisplayName = (student) =>
  student?.user?.name || student?.name || 'Unnamed Student';

const sortStudentsByDisplayName = (items) =>
  [...items].sort((a, b) =>
    getStudentDisplayName(a).localeCompare(getStudentDisplayName(b), undefined, { sensitivity: 'base' })
  );

export default function StudentPromotionPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [academicYearOptions, setAcademicYearOptions] = useState(generateFallbackAcademicYears());
  const [selectedClass, setSelectedClass] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showStudentList, setShowStudentList] = useState(false);
  const [lastPromotionResult, setLastPromotionResult] = useState(null);
  const [ignoreReportCardRepeats, setIgnoreReportCardRepeats] = useState(true);
  const [bulkTargetClassId, setBulkTargetClassId] = useState('');
  const [studentSearch, setStudentSearch] = useState('');

  const [form, setForm] = useState({
    fromYear: currentAcademicYearString(),
    toYear: nextAcademicYearString(),
    toTerm: 'Term 1',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'fromYear' ? { toYear: getNextAcademicYear(value) } : {}),
    }));
  };

  /* --------------------------
      Fetch Classes
  --------------------------- */

  const fetchClasses = useCallback(async () => {
    try {
      setLoadingInitial(true);
      const res = await api.get('/classes');

      let fetched = [];

      if (Array.isArray(res.data)) fetched = res.data;
      else if (Array.isArray(res.data.classes)) fetched = res.data.classes;
      else if (Array.isArray(res.data.data)) fetched = res.data.data;
      else {
        console.warn("Unexpected /classes response:", res.data);
        fetched = [];
      }

      setClasses(fetched);
    } catch (err) {
      console.error('Error fetching classes:', err);
      toast.error('Failed to load classes');
      setClasses([]);
    } finally {
      setLoadingInitial(false);
    }
  }, []);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  const fetchAcademicYears = useCallback(async () => {
    const schoolId = getStoredSchoolId();
    if (!schoolId) return;

    try {
      const res = await api.get('/term/years', { params: { school: schoolId } });
      const fetchedYears = Array.isArray(res.data?.academicYears) ? res.data.academicYears : [];
      if (fetchedYears.length === 0) return;

      const normalizedYears = fetchedYears
        .map(toAcademicYearString)
        .filter(Boolean)
        .sort((a, b) => b.localeCompare(a));
      const latestYear = normalizedYears[0];
      const options = [...new Set(normalizedYears)];

      setAcademicYearOptions(options);
      setForm((prev) => {
        const nextFromYear = normalizedYears.includes(prev.fromYear) ? prev.fromYear : latestYear;

        return {
          ...prev,
          fromYear: nextFromYear,
          toYear: getNextAcademicYear(nextFromYear),
        };
      });
    } catch (err) {
      console.error('Error fetching academic years:', err);
    }
  }, []);

  useEffect(() => {
    fetchAcademicYears();
  }, [fetchAcademicYears]);

  const getDestinationOptionsForClass = useCallback((classId) => {
    const sourceClass = classes.find((classItem) => classItem._id === classId);
    const nextBaseName = getNextClassBaseName(sourceClass);
    const nextClassOptions = classes.filter((classItem) => getClassBaseName(classItem) === nextBaseName);

    if (nextClassOptions.length > 0) {
      return sortClassesByDisplayName(nextClassOptions);
    }

    return sortClassesByDisplayName(classes.filter((classItem) => classItem._id !== classId));
  }, [classes]);

  const getDefaultTargetClassId = useCallback((classId) => {
    const options = getDestinationOptionsForClass(classId);
    return options.length === 1 ? options[0]._id : '';
  }, [getDestinationOptionsForClass]);

  /* --------------------------
      Fetch Students in Class
  --------------------------- */

  const fetchStudentsForClass = useCallback(async (classId) => {
    try {
      setLoadingStudents(true);
      const res = await api.get(`/students/class/${classId}`, {
        params: { academicYear: toAcademicYearString(form.fromYear) },
      });
      const list = res.data.students || res.data;
      const defaultTargetClassId = getDefaultTargetClassId(classId);

      setStudents(
        sortStudentsByDisplayName(list).map(s => ({
          ...s,
          selected: true,
          promote: true,
          targetClassId: defaultTargetClassId
        }))
      );
      setBulkTargetClassId(defaultTargetClassId);
      setShowStudentList(true);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load students");
    } finally {
      setLoadingStudents(false);
    }
  }, [form.fromYear, getDefaultTargetClassId]);

  const handleClassSelect = (e) => {
    const classId = e.target.value;
    setSelectedClass(classId);
    setLastPromotionResult(null);
    setBulkTargetClassId('');
    setStudentSearch('');

    if (classId === ALL_REPORT_CARD_CLASSES) {
      setStudents([]);
      setShowStudentList(false);
    }
  };

  useEffect(() => {
    if (selectedClass && selectedClass !== ALL_REPORT_CARD_CLASSES) {
      fetchStudentsForClass(selectedClass);
    }
  }, [fetchStudentsForClass, selectedClass]);

  /* --------------------------
      Build Migration Payload
  --------------------------- */

  const buildPayload = () => {
    const normalizedFromYear = toAcademicYearString(form.fromYear);
    const normalizedToYear = toAcademicYearString(form.toYear);

    if (selectedClass === ALL_REPORT_CARD_CLASSES) {
      return {
        fromYear: normalizedFromYear,
        toYear: normalizedToYear,
        toTerm: form.toTerm,
        promoteAllClasses: true
      };
    }

    // Full class migration (no student list loaded)
    if (selectedClass && students.length === 0) {
      return {
        fromYear: normalizedFromYear,
        toYear: normalizedToYear,
        toTerm: form.toTerm,
        classId: selectedClass
      };
    }

    // Individual student migration
    const selectedStudents = students
      .filter(s => s.selected)
      .map(s => ({
        studentId: s._id,
        promote: s.promote,
        targetClassId: s.promote ? s.targetClassId : undefined
      }));

    return {
      fromYear: normalizedFromYear,
      toYear: normalizedToYear,
      toTerm: form.toTerm,
      classId: selectedClass,
      students: selectedStudents
    };
  };

  /* --------------------------
      Submit Migration
  --------------------------- */

  const handleBulkDestinationChange = (targetClassId) => {
    setBulkTargetClassId(targetClassId);
    if (!targetClassId) return;

    setStudents((prev) =>
      prev.map((student) =>
        student.selected && student.promote
          ? { ...student, targetClassId }
          : student
      )
    );
  };

  const handlePromoteClick = () => {
    

    if (!selectedClass) {
      toast.warning("Please select a class");
      return;
    }

    if (selectedClass !== ALL_REPORT_CARD_CLASSES && students.length > 0 && students.every((student) => !student.selected)) {
      toast.warning("Please select at least one student to promote");
      return;
    }

    const studentsMissingDestination = students.filter(
      (student) => student.selected && student.promote && !student.targetClassId
    );
    if (selectedClass !== ALL_REPORT_CARD_CLASSES && studentsMissingDestination.length > 0) {
      toast.warning(`Select a destination class for ${studentsMissingDestination.length} promoted student(s).`);
      return;
    }

    setShowConfirmDialog(true);
  };

  const handlePromoteConfirm = async () => {
    setFormLoading(true);

    try {
      const payload = buildPayload();
      const res = await api.post('/migrate', payload);
      const result = res.data || {};
      setLastPromotionResult(result);

      if ((result.eligibleStudents || 0) === 0) {
        toast.warning(result.message || 'No eligible students were found for the selected class and year.');
      } else if ((result.modifiedStudents || 0) === 0) {
        toast.warning('Promotion request completed, but no student records were changed. Check the selected academic years.');
      } else {
        toast.success(result.message || 'Student promotion completed successfully!');
      }

      if (result.reportCardPromotionsApplied || result.reportCardRepeatsApplied) {
        toast.info(
          `Report-card promotion applied: ${result.reportCardPromotionsApplied || 0} promoted, ${result.reportCardRepeatsApplied || 0} repeated.`
        );
      }
      if (Array.isArray(result.unresolvedReportTargets) && result.unresolvedReportTargets.length > 0) {
        toast.warning(`${result.unresolvedReportTargets.length} report-card class name(s) could not be matched. Those students were skipped until the class exists.`);
      }
      if (Array.isArray(result.missingNextClassTargets) && result.missingNextClassTargets.length > 0) {
        toast.warning(`${result.missingNextClassTargets.length} student(s) were skipped because the next class is not available.`);
      }

      setShowConfirmDialog(false);
      if (selectedClass && selectedClass !== ALL_REPORT_CARD_CLASSES) {
        await fetchStudentsForClass(selectedClass);
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Student promotion failed');
    } finally {
      setFormLoading(false);
    }
  };

  const getTermColor = (term) => {
    switch (term) {
      case 'Term 1': return 'primary';
      case 'Term 2': return 'secondary';
      case 'Term 3': return 'success';
      default: return 'default';
    }
  };

  const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : 'S';

  // Stats calculation
  const totalStudents = students.length;
  const selectedStudents = students.filter(s => s.selected).length;
  const promotingStudents = students.filter(s => s.selected && s.promote).length;
  const repeatingStudents = students.filter(s => s.selected && !s.promote).length;
  const isAllClassPromotion = selectedClass === ALL_REPORT_CARD_CLASSES;
  const destinationClassOptions = selectedClass && !isAllClassPromotion
    ? getDestinationOptionsForClass(selectedClass)
    : [];
  const classNameById = classes.reduce((acc, classItem) => {
    acc[classItem._id] = getClassDisplayName(classItem);
    return acc;
  }, {});
  const destinationCounts = students
    .filter((student) => student.selected && student.promote && student.targetClassId)
    .reduce((acc, student) => {
      acc[student.targetClassId] = (acc[student.targetClassId] || 0) + 1;
      return acc;
    }, {});
  const normalizedStudentSearch = studentSearch.trim().toLowerCase();
  const visibleStudents = normalizedStudentSearch
    ? students.filter((student) =>
        getStudentDisplayName(student).toLowerCase().includes(normalizedStudentSearch)
      )
    : students;

  // Mobile Student Card Component
  const MobileStudentCard = ({ student }) => (
    <Card sx={{ mb: 2, boxShadow: 2 }}>
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          {/* Student Header */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" alignItems="center" spacing={1}>
              <StudentAvatar student={student} sx={{
                bgcolor: theme.palette.primary.main,
                width: 40,
                height: 40
              }}>
                {getInitials(student.user?.name || student.name)}
              </StudentAvatar>
              <Box>
                <Typography variant="subtitle1" fontWeight="600">
                  {student.user?.name || student.name || "Unnamed Student"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {student.user?.email || student.email || 'No email'}
                </Typography>
              </Box>
            </Stack>
          </Stack>

          {/* Controls */}
          <Stack spacing={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={student.selected}
                  onChange={(e) =>
                    setStudents(students.map(x =>
                      x._id === student._id ? { ...x, selected: e.target.checked } : x
                    ))
                  }
                  color="primary"
                  size="small"
                />
              }
              label="Include in promotion"
              sx={{ m: 0 }}
            />

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" fontWeight="500">
                Action:
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={student.promote}
                    onChange={(e) =>
                      setStudents(students.map(x =>
                        x._id === student._id
                          ? {
                              ...x,
                              promote: e.target.checked,
                              targetClassId: e.target.checked
                                ? x.targetClassId || getDefaultTargetClassId(selectedClass)
                                : x.targetClassId
                            }
                          : x
                      ))
                    }
                    color="success"
                    size="small"
                  />
                }
                label={student.promote ? "Promote" : "Repeat"}
                sx={{ m: 0 }}
              />
            </Stack>

            {student.selected && student.promote && (
              <TextField
                select
                fullWidth
                size="small"
                label="Destination class"
                value={student.targetClassId || ''}
                onChange={(e) =>
                  setStudents(students.map(x =>
                    x._id === student._id ? { ...x, targetClassId: e.target.value } : x
                  ))
                }
              >
                <MenuItem value="" disabled>Select destination</MenuItem>
                {destinationClassOptions.map((classItem) => (
                  <MenuItem key={classItem._id} value={classItem._id}>
                    {getClassDisplayName(classItem)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Stack>

          {/* Status */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Chip
              label={student.promote ? "Promoting" : "Repeating"}
              color={student.promote ? "success" : "warning"}
              variant="outlined"
              size="small"
              icon={student.promote ? <CheckCircle /> : <Warning />}
            />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  if (loadingInitial) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          py: isMobile ? 2 : 4,
          px: isMobile ? 1 : 2
        }}
      >
        <LoadingState />
      </Container>
    );
  }

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: isMobile ? 2 : 4,
        px: isMobile ? 1 : 2
      }}
    >

      {/* Header */}
      <PageHeader title={<>Student Promotion</>} description={<>Promote students to the next academic period</>}></PageHeader>
      {selectedClass && selectedClass !== ALL_REPORT_CARD_CLASSES && students.length > 0 && (
        <SummaryStrip container spacing={isMobile ? 2 : 3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 6, sm: 6, md: 6, lg: 3 }}>
            <SummaryMetric sx={{
              boxShadow: isMobile ? 1 : 3,
              border: `1px solid ${theme.palette.primary.main}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: isMobile ? 'none' : 'translateY(-4px)',
                boxShadow: isMobile ? 2 : 6
              }
            }}>
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar
                    sx={{
                      bgcolor: theme.palette.primary.main,
                      width: isMobile ? 36 : 48,
                      height: isMobile ? 36 : 48
                    }}
                  >
                    <Group fontSize={isMobile ? "small" : "medium"} />
                  </Avatar>
                  <Box>
                    <Typography
                      variant={isMobile ? "caption" : "h6"}
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? '0.75rem' : '1rem' }}
                    >
                      Total Students
                    </Typography>
                    <Typography
                      variant={isMobile ? "h5" : "h4"}
                      fontWeight="bold"
                      sx={{ fontSize: isMobile ? '1.25rem' : '2.125rem' }}
                    >
                      {totalStudents}
                    </Typography>
                    <Typography
                      variant={isMobile ? "caption" : "body2"}
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}
                    >
                      In selected class
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </SummaryMetric>
          </Grid>

          <Grid size={{ xs: 6, sm: 6, md: 6, lg: 3 }}>
            <SummaryMetric sx={{
              boxShadow: isMobile ? 1 : 3,
              border: `1px solid ${theme.palette.info.main}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: isMobile ? 'none' : 'translateY(-4px)',
                boxShadow: isMobile ? 2 : 6
              }
            }}>
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar
                    sx={{
                      bgcolor: theme.palette.info.main,
                      width: isMobile ? 36 : 48,
                      height: isMobile ? 36 : 48
                    }}
                  >
                    <CheckCircle fontSize={isMobile ? "small" : "medium"} />
                  </Avatar>
                  <Box>
                    <Typography
                      variant={isMobile ? "caption" : "h6"}
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? '0.75rem' : '1rem' }}
                    >
                      Selected
                    </Typography>
                    <Typography
                      variant={isMobile ? "h5" : "h4"}
                      fontWeight="bold"
                      sx={{ fontSize: isMobile ? '1.25rem' : '2.125rem' }}
                    >
                      {selectedStudents}
                    </Typography>
                    <Typography
                      variant={isMobile ? "caption" : "body2"}
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}
                    >
                      For promotion
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </SummaryMetric>
          </Grid>

          <Grid size={{ xs: 6, sm: 6, md: 6, lg: 3 }}>
            <SummaryMetric sx={{
              boxShadow: isMobile ? 1 : 3,
              border: `1px solid ${theme.palette.success.main}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: isMobile ? 'none' : 'translateY(-4px)',
                boxShadow: isMobile ? 2 : 6
              }
            }}>
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar
                    sx={{
                      bgcolor: theme.palette.success.main,
                      width: isMobile ? 36 : 48,
                      height: isMobile ? 36 : 48
                    }}
                  >
                    <PlaylistAddCheck fontSize={isMobile ? "small" : "medium"} />
                  </Avatar>
                  <Box>
                    <Typography
                      variant={isMobile ? "caption" : "h6"}
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? '0.75rem' : '1rem' }}
                    >
                      Promoting
                    </Typography>
                    <Typography
                      variant={isMobile ? "h5" : "h4"}
                      fontWeight="bold"
                      sx={{ fontSize: isMobile ? '1.25rem' : '2.125rem' }}
                    >
                      {promotingStudents}
                    </Typography>
                    <Typography
                      variant={isMobile ? "caption" : "body2"}
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}
                    >
                      Moving to next class
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </SummaryMetric>
          </Grid>

          <Grid size={{ xs: 6, sm: 6, md: 6, lg: 3 }}>
            <SummaryMetric sx={{
              boxShadow: isMobile ? 1 : 3,
              border: `1px solid ${theme.palette.warning.main}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: isMobile ? 'none' : 'translateY(-4px)',
                boxShadow: isMobile ? 2 : 6
              }
            }}>
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar
                    sx={{
                      bgcolor: theme.palette.warning.main,
                      width: isMobile ? 36 : 48,
                      height: isMobile ? 36 : 48
                    }}
                  >
                    <Warning fontSize={isMobile ? "small" : "medium"} />
                  </Avatar>
                  <Box>
                    <Typography
                      variant={isMobile ? "caption" : "h6"}
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? '0.75rem' : '1rem' }}
                    >
                      Repeating
                    </Typography>
                    <Typography
                      variant={isMobile ? "h5" : "h4"}
                      fontWeight="bold"
                      sx={{ fontSize: isMobile ? '1.25rem' : '2.125rem' }}
                    >
                      {repeatingStudents}
                    </Typography>
                    <Typography
                      variant={isMobile ? "caption" : "body2"}
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}
                    >
                      Staying in class
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </SummaryMetric>
          </Grid>
        </SummaryStrip>
      )}

      {/* Stats Cards - UPDATED GRID v2 */}
      

      <Card sx={{
        boxShadow: isMobile ? 1 : 3,
        border: `1px solid ${theme.palette.divider}`
      }}>
        <CardContent sx={{ p: isMobile ? 2 : isTablet ? 3 : 4 }}>

          {/* Migration Settings */}
          <Card variant="outlined" sx={{ mb: 4 }}>
            <CardContent sx={{ p: isMobile ? 2 : isTablet ? 3 : 4 }}>
              <Stack
                direction={isMobile ? "column" : "row"}
                alignItems={isMobile ? "flex-start" : "center"}
                spacing={isMobile ? 2 : 2}
                sx={{ mb: 3 }}
              >
                <Avatar
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    width: isMobile ? 40 : 48,
                    height: isMobile ? 40 : 48
                  }}
                >
                  <CalendarToday fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Typography
                  variant={isMobile ? "h6" : "h5"}
                  fontWeight="600"
                  sx={{ fontSize: isMobile ? '1.125rem' : '1.5rem' }}
                >
                  Promotion Settings
                </Typography>
              </Stack>

              {/* Migration Settings Grid - UPDATED GRID v2 */}
              <Grid container spacing={isMobile ? 2 : 3}>
                {/* Class Selector */}
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    select
                    label="Select Class To Promote"
                    value={selectedClass}
                    onChange={handleClassSelect}
                    helperText="Choose the class you want to promote students from"
                    size={isMobile ? "small" : "medium"}
                  >
                    <MenuItem value="" disabled>Select Class</MenuItem>
                    <MenuItem value={ALL_REPORT_CARD_CLASSES}>
                      All classes with report-card promotions
                    </MenuItem>
                    {classes.map((c) => (
                      <MenuItem key={c._id} value={c._id}>
                        {c.displayName || c.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    Promotion only loads and moves students in the selected <strong>From Academic Year</strong>.
                    The <strong>To Academic Year</strong> is generated automatically, even if that academic year has not been created yet.
                    Choose <strong>All classes with report-card promotions</strong> to apply all prepared report-card promotion values at once.
                  </Alert>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Paper className="workspace-section"
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      bgcolor: (theme) => alpha(theme.palette.success.main, 0.04),
                      borderColor: (theme) => alpha(theme.palette.success.main, 0.25),
                    }}
                  >
                    <Box sx={{ pr: 2 }}>
                      <Typography variant="subtitle2" fontWeight="700">
                        Ignore report-card "Repeated" remarks
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        When active, students marked as "Repeated" on old report cards will not be held back and will be promoted to the next class.
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={ignoreReportCardRepeats}
                          onChange={(e) => setIgnoreReportCardRepeats(e.target.checked)}
                          color="success"
                        />
                      }
                      label={ignoreReportCardRepeats ? "Active" : "Off"}
                      sx={{ m: 0, whiteSpace: 'nowrap' }}
                    />
                  </Paper>
                </Grid>

                {/* Source academic period */}
                <Grid size={{ xs: 12 }}>
                  <TextField
                    select
                    fullWidth
                    label="From Academic Year"
                    name="fromYear"
                    value={form.fromYear}
                    onChange={handleChange}
                    helperText="Current academic year"
                    size={isMobile ? "small" : "medium"}
                  >
                    {academicYearOptions.map(year => (
                      <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                  </TextField>

                  
                </Grid>

                {!isMobile && (
                  <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: theme.palette.info.main, width: 48, height: 48 }}>
                      <ArrowForward />
                    </Avatar>
                  </Grid>
                )}

                {/* Destination academic period */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="To Academic Year"
                    name="toYear"
                    value={form.toYear}
                    helperText="Auto-created for promotion. The next academic year does not need to exist yet."
                    size={isMobile ? "small" : "medium"}
                    sx={{ mt: isMobile ? 2 : 0 }}
                    InputProps={{
                      readOnly: true,
                    }}
                  />

                  
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    select
                    fullWidth
                    label="To Term"
                    name="toTerm"
                    value={form.toTerm}
                    onChange={handleChange}
                    helperText="Term students will start after promotion"
                    size={isMobile ? "small" : "medium"}
                    sx={{ mt: isMobile ? 2 : 0 }}
                  >
                    {terms.map(term => (
                      <MenuItem key={term} value={term}>{term}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {lastPromotionResult && (
            <Alert
              severity={(lastPromotionResult.modifiedStudents || 0) > 0 ? "success" : "warning"}
              sx={{ mb: 4, borderRadius: 2 }}
            >
              <Typography variant="body2" fontWeight="700" gutterBottom>
                Last promotion result
              </Typography>
              <Typography variant="body2">
                Eligible students: {lastPromotionResult.eligibleStudents || 0} · Student records changed: {lastPromotionResult.modifiedStudents || 0}
              </Typography>
              <Typography variant="body2">
                Report-card moves: {lastPromotionResult.reportCardPromotionsApplied || 0} · Repeated: {lastPromotionResult.reportCardRepeatsApplied || 0} · Normal next-class moves: {lastPromotionResult.fallbackPromotionsApplied || 0}
              </Typography>
              <Typography variant="body2">
                Manual placements: {lastPromotionResult.manualPlacementsApplied || 0}
              </Typography>
              <Typography variant="body2">
                Skipped unresolved report-card targets: {lastPromotionResult.skippedUnresolvedReportTarget || 0}
              </Typography>
              <Typography variant="body2">
                Skipped missing next class: {lastPromotionResult.skippedMissingNextClass || 0}
              </Typography>
              {lastPromotionResult.promoteAllClasses && (
                <Typography variant="body2">
                  Skipped without report-card promotion: {lastPromotionResult.skippedNoReportCardPromotion || 0}
                </Typography>
              )}
              {lastPromotionResult.sourceTerm && (
                <Typography variant="body2">
                  Report-card source: {lastPromotionResult.sourceTerm.term} {lastPromotionResult.sourceTerm.academicYear}
                </Typography>
              )}
            </Alert>
          )}
 
          {/* Student List */}
          {selectedClass && selectedClass !== ALL_REPORT_CARD_CLASSES && (
            <Card variant="outlined" sx={{ mb: 4 }}>
              <CardContent sx={{ p: 0 }}>
                <Box
                  sx={{
                    p: isMobile ? 2 : 3,
                    borderBottom: `1px solid ${theme.palette.divider}`
                  }}
                >
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    alignItems={isMobile ? "flex-start" : "center"}
                    justifyContent="space-between"
                    spacing={isMobile ? 2 : 0}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar
                        sx={{
                          bgcolor: theme.palette.info.main,
                          width: isMobile ? 36 : 40,
                          height: isMobile ? 36 : 40
                        }}
                      >
                        <Group fontSize={isMobile ? "small" : "medium"} />
                      </Avatar>
                      <Box>
                        <Typography
                          variant={isMobile ? "h6" : "h5"}
                          fontWeight="600"
                          sx={{ fontSize: isMobile ? '1rem' : '1.5rem' }}
                        >
                          Students in Selected Class
                        </Typography>
                        <Typography
                          variant={isMobile ? "caption" : "body2"}
                          color="text.secondary"
                          sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
                        >
                          Manage student promotion settings
                        </Typography>
                      </Box>
                    </Stack>
                    <Button
                      variant="outlined"
                      size={isMobile ? "small" : "medium"}
                      startIcon={showStudentList ? <ExpandLess /> : <ExpandMore />}
                      onClick={() => setShowStudentList(!showStudentList)}
                      sx={{ mt: isMobile ? 1 : 0 }}
                    >
                      {showStudentList ? 'Hide Students' : 'Show Students'}
                    </Button>
                  </Stack>
                </Box>

                <Collapse in={showStudentList}>
                  <Box sx={{ p: isMobile ? 2 : 3 }}>
                    {loadingStudents ? (
                      <LoadingState />
                    ) : students.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
                        <Person
                          sx={{
                            fontSize: isMobile ? 48 : 64,
                            color: 'text.secondary',
                            opacity: 0.5,
                            mb: 2
                          }}
                        />
                        <Typography
                          variant={isMobile ? "h6" : "h6"}
                          color="text.secondary"
                          gutterBottom
                          sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
                        >
                          No students found
                        </Typography>
                        <Typography
                          variant={isMobile ? "body2" : "body2"}
                          color="text.secondary"
                          sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
                        >
                          No students found in this class for {form.fromYear}. If the next academic year has already been created, choose the previous academic year as the source.
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <Paper className="workspace-section"
                          variant="outlined"
                          sx={{
                            p: 2,
                            mb: 2,
                            borderRadius: 2,
                            bgcolor: (theme) => alpha(theme.palette.info.main, 0.04),
                          }}
                        >
                          <Grid container spacing={2} alignItems="center">
                            <Grid size={{ xs: 12, md: 4 }}>
                              <TextField
                                fullWidth
                                size={isMobile ? "small" : "medium"}
                                label="Search learner name"
                                value={studentSearch}
                                onChange={(e) => setStudentSearch(e.target.value)}
                                placeholder="Type a student name"
                                helperText={`${visibleStudents.length} of ${students.length} shown`}
                              />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                              <TextField
                                select
                                fullWidth
                                size={isMobile ? "small" : "medium"}
                                label="Set destination for selected promoted learners"
                                value={bulkTargetClassId}
                                onChange={(e) => handleBulkDestinationChange(e.target.value)}
                              >
                                <MenuItem value="">Choose destination</MenuItem>
                                {destinationClassOptions.map((classItem) => (
                                  <MenuItem key={classItem._id} value={classItem._id}>
                                    {getClassDisplayName(classItem)}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {Object.keys(destinationCounts).length === 0 ? (
                                  <Chip label="No destinations selected yet" variant="outlined" color="warning" />
                                ) : (
                                  Object.entries(destinationCounts).map(([classId, count]) => (
                                    <Chip
                                      key={classId}
                                      label={`${classNameById[classId] || 'Selected class'}: ${count}`}
                                      color="info"
                                      variant="outlined"
                                    />
                                  ))
                                )}
                              </Stack>
                            </Grid>
                          </Grid>
                        </Paper>

                        {visibleStudents.length === 0 ? (
                          <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
                            <Person
                              sx={{
                                fontSize: isMobile ? 40 : 52,
                                color: 'text.secondary',
                                opacity: 0.5,
                                mb: 2
                              }}
                            />
                            <Typography variant="subtitle1" color="text.secondary" fontWeight="600">
                              No learner matches this search
                            </Typography>
                          </Box>
                        ) : (
                          <>
                        {isMobile ? (
                      // Mobile View - Cards
                      <Box>
                        <Stack spacing={1}>
                          {visibleStudents.map((student) => (
                            <MobileStudentCard key={student._id} student={student} />
                          ))}
                        </Stack>
                      </Box>
                        ) : (
                      // Desktop/Tablet View - Table
                      <Box sx={{
                        width: '100%',
                        overflow: 'auto',
                        maxHeight: isTablet ? '60vh' : 'none'
                      }}>
                        <DataTable><Table sx={{ minWidth: isTablet ? 700 : 'auto' }}>
                          <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold', py: 2 }}>STUDENT</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', py: 2, textAlign: 'center' }}>INCLUDE</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', py: 2, textAlign: 'center' }}>ACTION</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', py: 2 }}>DESTINATION CLASS</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', py: 2, textAlign: 'center' }}>STATUS</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
{visibleStudents.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

                            {visibleStudents.map((student) => (
                              <TableRow
                                key={student._id}
                                sx={{
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.02)
                                  }
                                }}
                              >
                                <TableCell sx={{ py: 2 }}>
                                  <Stack direction="row" alignItems="center" spacing={2}>
                                    <StudentAvatar student={student} sx={{ bgcolor: theme.palette.primary.main, width: 40, height: 40 }}>
                                      {getInitials(student.user?.name || student.name)}
                                    </StudentAvatar>
                                    <Box>
                                      <Typography variant="subtitle1" fontWeight="500">
                                        {student.user?.name || student.name || "Unnamed Student"}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        {student.user?.email || student.email || 'No email'}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                </TableCell>

                                <TableCell sx={{ py: 2, textAlign: 'center' }}>
                                  <Checkbox
                                    checked={student.selected}
                                    onChange={(e) =>
                                      setStudents(students.map(x =>
                                        x._id === student._id ? { ...x, selected: e.target.checked } : x
                                      ))
                                    }
                                    color="primary"
                                  />
                                </TableCell>

                                <TableCell sx={{ py: 2, textAlign: 'center' }}>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={student.promote}
                                        onChange={(e) =>
                                          setStudents(students.map(x =>
                                            x._id === student._id
                                              ? {
                                                  ...x,
                                                  promote: e.target.checked,
                                                  targetClassId: e.target.checked
                                                    ? x.targetClassId || getDefaultTargetClassId(selectedClass)
                                                    : x.targetClassId
                                                }
                                              : x
                                          ))
                                        }
                                        color="success"
                                      />
                                    }
                                    label={student.promote ? "Promote" : "Repeat"}
                                  />
                                </TableCell>

                                <TableCell sx={{ py: 2, minWidth: 220 }}>
                                  <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    value={student.targetClassId || ''}
                                    disabled={!student.selected || !student.promote}
                                    onChange={(e) =>
                                      setStudents(students.map(x =>
                                        x._id === student._id ? { ...x, targetClassId: e.target.value } : x
                                      ))
                                    }
                                  >
                                    <MenuItem value="">Select destination</MenuItem>
                                    {destinationClassOptions.map((classItem) => (
                                      <MenuItem key={classItem._id} value={classItem._id}>
                                        {getClassDisplayName(classItem)}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                </TableCell>

                                <TableCell sx={{ py: 2, textAlign: 'center' }}>
                                  <Chip
                                    label={student.promote ? "Promoting" : "Repeating"}
                                    color={student.promote ? "success" : "warning"}
                                    variant="outlined"
                                    icon={student.promote ? <CheckCircle /> : <Warning />}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table></DataTable>
                      </Box>
                        )}
                          </>
                        )}
                      </>
                    )}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          )}

          {/* Action Buttons */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            mt: 3,
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            <Button
              variant="outlined"
              size={isMobile ? "medium" : "large"}
              onClick={() => {
                setSelectedClass("");
                setStudents([]);
                setShowStudentList(false);
                setLastPromotionResult(null);
                setStudentSearch('');
              }}
              disabled={formLoading}
              fullWidth={isMobile}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              size={isMobile ? "medium" : "large"}
              disabled={formLoading || !selectedClass}
              onClick={handlePromoteClick}
              startIcon={formLoading ? <CircularProgress size={20} /> : <School />}
              fullWidth={isMobile}
            >
              {formLoading ? 'Processing...' : isAllClassPromotion ? 'Promote All Classes' : 'Promote Students'}
            </Button>
          </Box>

        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
            m: isMobile ? 0 : 'auto'
          }
        }}
      >
        <DialogTitle sx={{
          bgcolor: theme.palette.success.main,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: isMobile ? 2 : 3
        }}>
          <School />
          Confirm Student Promotion
        </DialogTitle>
        <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
          {isAllClassPromotion ? (
            <Typography gutterBottom>
              Are you sure you want to apply report-card promotions for <strong>all classes</strong> from{' '}
              <strong>{form.fromYear}</strong> to{' '}
              <strong>{form.toYear} - {form.toTerm}</strong>?
            </Typography>
          ) : (
            <Typography gutterBottom>
              Are you sure you want to promote students from{' '}
              <strong>{form.fromYear}</strong> to{' '}
              <strong>{form.toYear} - {form.toTerm}</strong>?
            </Typography>
          )}

          {isAllClassPromotion && (
            <Alert severity="info" sx={{ mt: 2, borderRadius: isMobile ? 1 : 2 }}>
              Only students with a report-card <strong>Promoted To</strong> value will be moved. Students without that value will be skipped.
            </Alert>
          )}

          {!isAllClassPromotion && students.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2, borderRadius: isMobile ? 1 : 2 }}>
                <Typography variant="body2">
                  <strong>Promotion Summary:</strong>
                </Typography>
                <Typography variant="body2">
                  • {promotingStudents} students will be promoted
                </Typography>
                <Typography variant="body2">
                  • {repeatingStudents} students will repeat the class
                </Typography>
                <Typography variant="body2">
                  • {totalStudents - selectedStudents} students will be excluded
                </Typography>
                {Object.entries(destinationCounts).map(([classId, count]) => (
                  <Typography key={classId} variant="body2">
                    • {count} to {classNameById[classId] || 'selected destination'}
                  </Typography>
                ))}
              </Alert>
            </Box>
          )}

          <Alert severity="warning" sx={{ mt: 2, borderRadius: isMobile ? 1 : 2 }}>
            This action cannot be undone. Student records will be permanently updated.
          </Alert>
        </DialogContent>
        <DialogActions sx={{
          p: isMobile ? 2 : 3,
          gap: 1,
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <Button
            onClick={() => setShowConfirmDialog(false)}
            variant="outlined"
            disabled={formLoading}
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePromoteConfirm}
            variant="contained"
            color="success"
            disabled={formLoading}
            startIcon={formLoading ? <CircularProgress size={20} /> : null}
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
          >
            {formLoading ? 'Promoting...' : 'Confirm Promotion'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
